import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import SEO, { breadcrumbs } from '../components/common/SEO'
import Disclosure from '../components/common/Disclosure'
import ResponsiveImage from '../components/common/ResponsiveImage'
import { BOOKING_URL, SITE_URL } from '../data/site'
import { massagePricing, athleticPricing, naturopathicPricing } from '../data/servicePages'

const warmLinenTexture = {
  dotLayer: {
    backgroundImage:
      'radial-gradient(circle at 1px 1px, rgba(109,42,20,0.07) 1px, transparent 0)',
    backgroundSize: '14px 14px',
  },
  lineLayer: {
    backgroundImage:
      'repeating-linear-gradient(90deg, rgba(47,40,34,0.06), rgba(47,40,34,0.06) 1px, transparent 1px, transparent 22px)',
  },
}

const serviceBlocks = [
  {
    key: 'massage-therapy',
    title: 'Massage\nTherapy',
    tagline:
      'Hands-on treatment to help reduce pain, relieve tension, improve mobility, and support recovery.',
    description: [
      'Our massage therapists are experts in hands-on care and utilize a combination of manual therapy techniques that are best suited for your goals. Your RMT may incorporate deep tissue massage, joint mobilizations, cupping, acupuncture, and corrective exercises.',
    ],
    image: '/services/massage-therapy.jpg',
    imageAlt:
      'Registered massage therapist applying deep tissue work to a client\u2019s shoulder',
    concerns: [
      'Neck & back pain',
      'Headaches and jaw tension',
      'Stress and burnout',
      'Pregnancy and postpartum care',
      'General maintenance and recovery',
    ],
    pricing: massagePricing,
    bookLabel: 'Book Massage Therapy',
  },
  {
    key: 'athletic-therapy',
    title: 'Athletic\nTherapy',
    description: [
      'Athletic Therapy specializes in the prevention, assessment, and treatment of musculoskeletal injuries and conditions. Using a combination of hands-on techniques and therapeutic exercise, Athletic Therapists help manage pain, restore movement and strength, and prevent future injuries. Following the Sports Medicine Model of care, Certified Athletic Therapists work with clients to move better, recover confidently, and return to the activities they love.',
    ],
    image: '/services/athletic-therapy.jpg',
    imageAlt:
      'Athletic therapist guiding a client through a functional movement exercise',
    concernsTitle: 'Who can benefit from Athletic Therapy?',
    concerns: [
      {
        label: 'Athletes',
        detail: 'from recreational to high-performance',
      },
      {
        label: 'Active Individuals',
        detail:
          'anyone dealing with an injury or looking to improve strength, mobility & performance',
      },
      {
        label: 'Desk Workers',
        detail:
          'individuals experiencing neck, shoulder, back or postural-related discomfort',
      },
      {
        label: '“Athletes of Life”',
        detail:
          'individuals who want to stay active & capable in their everyday lives',
      },
      {
        label: 'Individuals Recovering from Injuries',
        detail:
          'including sprains, strains, joint-related injuries/conditions, muscle-related injuries/conditions & any other musculoskeletal (MSK) conditions',
      },
      {
        label: 'Pre- & Post-Surgical Clients',
        detail:
          'to strengthen prior to surgery or to assist with movement and function post-surgery',
      },
      {
        label: 'Individuals looking to Prevent Injuries',
        detail:
          'through strength, mobility, movement assessment & education',
      },
    ],
    pricing: athleticPricing,
    bookLabel: 'Book Athletic Therapy',
  },
  {
    key: 'naturopathic-medicine',
    title: 'Naturopathic\nMedicine',
    description: [
      'Naturopathic medicine considers the many factors that influence how you feel and function every day, including sleep, nutrition, stress, movement, hormones, and lifestyle habits. By addressing these foundations of health, treatment is designed to support recovery, build resilience, and help you feel your best over the long term.',
      'Care may include nutrition and lifestyle guidance, acupuncture, botanical medicine, supplementation, and laboratory testing, depending on your needs and goals.',
    ],
    image: '/services/consultation.jpg',
    imageAlt: 'Naturopathic doctor consulting with a patient in a sunlit treatment room',
    concerns: [
      'Fatigue, low energy, high stress, sleep issues',
      'Hormonal health (PMS, PCOS, fertility, thyroid, perimenopause, menstrual concerns)',
      'Stress & nervous system regulation',
      'Digestive health',
      'Blood sugar balance & metabolic health (weight loss, cholesterol, blood pressure)',
      'Skin health',
      'Pain management',
    ],
    pricing: naturopathicPricing,
    bookLabel: 'Book Naturopathic Medicine',
  },
]

const specialtyTreatments = [
  {
    name: 'Neurofunctional Acupuncture',
    slug: 'acupuncture',
    description:
      'Targeted treatment used to help manage pain, support recovery, improve sleep, and regulate the nervous system.',
    image: '/services/acupuncture.jpg',
  },
  {
    name: 'TMJ & Jaw Care',
    slug: 'tmj-treatment',
    description:
      'Support for jaw pain, clenching, headaches, facial tension, and related neck discomfort.',
    image: '/services/pain-management.jpg',
  },
  {
    name: 'Prenatal & Postpartum Care',
    slug: 'prenatal-massage',
    description:
      'Treatment designed to support the changing demands of pregnancy and postpartum recovery.',
    image: '/services/prenatal.jpg',
  },
  {
    name: 'Cupping Therapy',
    description:
      'Used to help relieve tension, improve mobility, and complement hands-on treatment.',
    image: '/services/cupping.jpg',
  },
  {
    name: 'Sports & Active Recovery',
    slug: 'sports-massage',
    description:
      'Support for training, performance, recovery, and maintaining an active lifestyle.',
    image: '/services/sports-massage.jpg',
  },
]

const ServicesPage = () => {
  return (
    <>
      <SEO
        title="Services & Pricing — Massage & Athletic Therapy"
        description="Massage therapy, athletic therapy, naturopathic medicine, acupuncture, and TMJ care at Yonge & Eglinton, with clear duration-based pricing."
        path="/services"
        keywords="massage therapy pricing Toronto, athletic therapy Toronto, RMT rates Yonge Eglinton, naturopathic medicine Toronto, acupuncture Toronto, TMJ treatment cost"
        structuredData={[
          breadcrumbs([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'OfferCatalog',
            name: 'Recreo Rehab Services',
            provider: { '@id': `${SITE_URL}/#business` },
            itemListElement: [
              // Core services with duration-based pricing.
              ...serviceBlocks.map((block, i) => ({
                '@type': 'Offer',
                position: i + 1,
                itemOffered: {
                  '@type': 'MedicalTherapy',
                  name: block.title.replace(/\n/g, ' '),
                  description: block.tagline || block.description?.[0],
                  provider: { '@id': `${SITE_URL}/#business` },
                },
                priceCurrency: 'CAD',
                priceSpecification: block.pricing
                  .filter((p) => /\$\s*\d/.test(p.price))
                  .map((p) => ({
                    '@type': 'UnitPriceSpecification',
                    name: p.duration,
                    price: p.price.replace(/[^0-9.]/g, ''),
                    priceCurrency: 'CAD',
                  })),
                url: `${SITE_URL}/services/${block.key}`,
                availability: 'https://schema.org/InStock',
              })),
              // Specialty treatments (no standalone pricing).
              ...specialtyTreatments.map((treatment, i) => ({
                '@type': 'Offer',
                position: serviceBlocks.length + i + 1,
                itemOffered: {
                  '@type': 'MedicalTherapy',
                  name: treatment.name,
                  description: treatment.description,
                  provider: { '@id': `${SITE_URL}/#business` },
                },
                url: treatment.slug ? `${SITE_URL}/services/${treatment.slug}` : `${SITE_URL}/services`,
                availability: 'https://schema.org/InStock',
              })),
            ],
          },
        ]}
      />

      {/* Earth Grain hero */}
      <div className="bg-cream-200">
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#d8c4ac] overflow-hidden rounded-b-[24px] md:rounded-b-[100px] z-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 22% 18%, rgba(197,72,25,0.18), transparent 35%), radial-gradient(circle at 80% 70%, rgba(0,82,0,0.12), transparent 45%)',
            }}
            aria-hidden="true"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
              opacity: 0.12,
              mixBlendMode: 'multiply',
            }}
            aria-hidden="true"
          />

          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
            <motion.p
              className="font-script text-dark-600 italic text-base md:text-lg mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            >
              What we offer
            </motion.p>
            <motion.h1
              className="font-display text-dark-800 text-4xl md:text-6xl lg:text-7xl leading-[1.1]"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4, ease: 'easeOut' }}
            >
              Massage, Athletic Therapy &amp; Naturopathic Care in Midtown Toronto
            </motion.h1>
          </div>
        </section>
      </div>

      {/* Service panels — alternating plain cream / warm linen backgrounds */}
      <div className="w-full flex flex-col">
        {serviceBlocks.map((block, index) => {
          const isImageLeft = index % 2 === 0
          const isEven = index % 2 === 0
          const bgColorClass = isEven ? 'bg-cream-100' : 'bg-cream-300'
          const showLinen = !isEven

          return (
            <section key={block.key} className={`relative ${bgColorClass} py-16 md:py-24`}>
              {showLinen && (
                <>
                  <div
                    className="absolute inset-0"
                    style={warmLinenTexture.dotLayer}
                    aria-hidden="true"
                  />
                  <div
                    className="absolute inset-0"
                    style={warmLinenTexture.lineLayer}
                    aria-hidden="true"
                  />
                </>
              )}
              <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start"
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                  {/* Image */}
                  <div className={`${isImageLeft ? 'md:order-1' : 'md:order-2'} md:sticky md:top-28`}>
                    <div className="rounded-[20px] overflow-hidden shadow-lg">
                      <ResponsiveImage
                        src={block.image}
                        alt={block.imageAlt}
                        sizes="(min-width: 768px) 520px, 100vw"
                        className="w-full aspect-[4/5] object-cover"
                      />
                    </div>
                  </div>

                  {/* Text */}
                  <div className={`${isImageLeft ? 'md:order-2' : 'md:order-1'}`}>
                    <h2 className="font-display text-dark-700 text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1] mb-6">
                      {block.title.split('\n').map((line, i) => (
                        <span key={i}>
                          {line}
                          {i < block.title.split('\n').length - 1 && (
                            <>
                              {' '}
                              <br />
                            </>
                          )}
                        </span>
                      ))}
                    </h2>

                    {block.tagline && (
                      <p className="font-sans text-dark-700 text-lg md:text-xl font-medium leading-relaxed mb-5">
                        {block.tagline}
                      </p>
                    )}

                    {block.description.map((paragraph, i) => (
                      <p
                        key={i}
                        className="font-sans text-dark-500 text-base md:text-lg leading-relaxed mb-4"
                      >
                        {paragraph}
                      </p>
                    ))}

                    {/* Accordions */}
                    <div className="mt-6 mb-8 border-b border-dark-300/40">
                      <Disclosure title={block.concernsTitle || 'Common concerns we support'}>
                        <ul className="space-y-2.5">
                          {block.concerns.map((concern) => {
                            const isObject = typeof concern === 'object'
                            const key = isObject ? concern.label : concern
                            return (
                              <li
                                key={key}
                                className="flex items-start gap-3 font-sans text-dark-500 text-base leading-relaxed"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-brand-orange-500 mt-2 flex-shrink-0" />
                                {isObject ? (
                                  <span>
                                    <span className="font-medium text-dark-700">
                                      {concern.label}
                                    </span>
                                    {' — '}
                                    {concern.detail}
                                  </span>
                                ) : (
                                  concern
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      </Disclosure>

                      <Disclosure title="Pricing" defaultOpen>
                        <ul className="divide-y divide-dark-200/40">
                          {block.pricing.map((item) => (
                            <li
                              key={item.duration}
                              className="flex justify-between items-baseline gap-4 py-3"
                            >
                              <span className="font-sans text-dark-600 text-base">
                                {item.duration}
                              </span>
                              <span className="font-display text-dark-700 text-lg font-medium flex-shrink-0">
                                {item.price}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </Disclosure>
                    </div>

                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-brand-orange-500 text-white px-8 py-3 rounded-full font-sans text-[11px] font-medium tracking-[0.15em] uppercase hover:bg-brand-orange-400 transition-colors duration-200 shadow-sm"
                    >
                      {block.bookLabel}
                    </a>
                    <Link
                      to={`/services/${block.key}`}
                      className="inline-block ml-4 mt-3 font-sans text-xs tracking-[0.15em] uppercase text-dark-600 hover:text-brand-orange-500 underline underline-offset-4"
                    >
                      Learn more
                      <span className="sr-only"> about {block.title.replace('\n', ' ')}</span>
                    </Link>
                  </div>
                </motion.div>
              </div>
            </section>
          )
        })}
      </div>

      {/* Specialty Treatments */}
      <section className="bg-cream-100 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="max-w-2xl mb-12 md:mb-16"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <span className="font-script text-brand-orange-500 italic text-lg block mb-3">
              Specialty Treatments
            </span>
            <h2 className="font-display text-dark-700 text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.15] mb-5">
              Specialized techniques, tailored to you
            </h2>
            <p className="font-sans text-dark-500 text-base md:text-lg leading-relaxed">
              Our practitioners may incorporate specialized techniques and
              treatment approaches based on your needs and goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {specialtyTreatments.map((treatment, index) => (
              <motion.div
                key={treatment.name}
                className="group relative bg-white rounded-[20px] overflow-hidden border border-dark-200/25 shadow-sm flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.1, ease: 'easeOut' }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <ResponsiveImage
                    src={treatment.image}
                    alt={treatment.name}
                    sizes="(min-width: 1024px) 360px, (min-width: 640px) 50vw, 100vw"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 md:p-7 flex flex-col flex-grow">
                  <h3 className="font-display text-dark-700 text-xl font-semibold mb-2">
                    {treatment.slug ? (
                      <Link
                        to={`/services/${treatment.slug}`}
                        className="group-hover:text-brand-orange-500 transition-colors after:absolute after:inset-0"
                      >
                        {treatment.name}
                      </Link>
                    ) : (
                      treatment.name
                    )}
                  </h3>
                  <p className="font-sans text-dark-500 text-base leading-relaxed">
                    {treatment.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 md:mt-16 text-center">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full bg-dark-700 text-cream-100 px-9 py-3.5 font-sans text-xs font-semibold tracking-[0.25em] uppercase shadow-md hover:bg-dark-800 hover:shadow-lg transition-all duration-300"
            >
              Book a session
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </section>
    </>
  )
}

export default ServicesPage
